import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Pin, PinOff, Pencil, Trash2, AlertTriangle, Check, X } from 'lucide-react';
import { Badge } from '../ui';
import { cn } from '../../lib/utils';
import {
  updateTenantNote,
  deleteTenantNote,
  getTenantNoteCategoryVariant,
  TENANT_NOTE_CATEGORY_LABELS,
} from '../../services';
import type { TenantNote } from '../../data/types';

interface NoteCardProps {
  note: TenantNote;
  canModify: boolean;
  onUpdated: (note: TenantNote) => void;
  onDeleted: (noteId: string) => void;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export function NoteCard({ note, canModify, onUpdated, onDeleted }: NoteCardProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.content);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const saveEdit = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === note.content) {
      setEditing(false);
      setDraft(note.content);
      return;
    }
    setBusy(true);
    const r = await updateTenantNote(note.tenantId, note.noteId, { content: trimmed });
    setBusy(false);
    if (r.success) {
      onUpdated(r.data);
      setEditing(false);
    }
  };

  const togglePin = async () => {
    setBusy(true);
    const r = await updateTenantNote(note.tenantId, note.noteId, { isPinned: !note.isPinned });
    setBusy(false);
    if (r.success) onUpdated(r.data);
  };

  const doDelete = async () => {
    setBusy(true);
    const r = await deleteTenantNote(note.tenantId, note.noteId);
    setBusy(false);
    if (r.success) onDeleted(note.noteId);
  };

  const edited = note.updatedAt !== note.createdAt;

  return (
    <div
      className={cn(
        'rounded-xl border border-border-default bg-bg-secondary p-4',
        note.isPinned && 'ring-1 ring-brand-lime/40'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          {note.category && (
            <Badge variant={getTenantNoteCategoryVariant(note.category)}>
              {TENANT_NOTE_CATEGORY_LABELS[note.category]}
            </Badge>
          )}
          {note.isPinned && (
            <Badge variant="outline">
              <Pin className="w-3 h-3" /> Pinned
            </Badge>
          )}
          {note.incidentId && (
            <Link
              to={`/incidents/${note.tenantId}/${note.incidentId}`}
              className="inline-flex items-center gap-1 text-xs text-accent-blue hover:underline"
            >
              <AlertTriangle className="w-3 h-3" /> Incident {note.incidentId}
            </Link>
          )}
        </div>

        {canModify && !editing && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={togglePin}
              disabled={busy}
              title={note.isPinned ? 'Unpin' : 'Pin'}
              className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-bg-tertiary transition-colors"
            >
              {note.isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(true);
                setDraft(note.content);
              }}
              disabled={busy}
              title="Edit"
              className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-bg-tertiary transition-colors"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
              title="Delete"
              className="p-1.5 rounded-lg text-text-muted hover:text-red-600 dark:hover:text-red-400 hover:bg-bg-tertiary transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="mt-3 space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 bg-bg-tertiary border border-border-default rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-blue resize-none"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setDraft(note.content);
              }}
              className="px-2.5 py-1.5 rounded-lg text-xs text-text-secondary hover:bg-bg-tertiary flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Cancel
            </button>
            <button
              type="button"
              onClick={saveEdit}
              disabled={busy}
              className="px-2.5 py-1.5 rounded-lg text-xs bg-brand-navy dark:bg-brand-lime text-white dark:text-brand-navy flex items-center gap-1 disabled:opacity-60"
            >
              <Check className="w-3 h-3" /> Save
            </button>
          </div>
        </div>
      ) : (
        <p className="mt-2 text-sm text-text-primary whitespace-pre-wrap break-words">
          {note.content}
        </p>
      )}

      <div className="mt-3 flex items-center gap-2 text-xs text-text-muted">
        <span className="font-medium text-text-secondary">{note.authorName}</span>
        <span>&middot;</span>
        <span>{formatTimestamp(note.createdAt)}</span>
        {edited && <span>&middot; edited</span>}
      </div>

      {confirmDelete && (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-red-50 dark:bg-red-900/20 px-3 py-2">
          <span className="text-xs text-red-700 dark:text-red-400">Delete this note?</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="px-2 py-1 rounded text-xs text-text-secondary hover:bg-bg-tertiary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={doDelete}
              disabled={busy}
              className="px-2 py-1 rounded text-xs bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
